// Thin fetch wrapper for the agent's JSON API.
//
// Every call shares the session cookie. On 401 the wrapper invokes the
// globally-registered onUnauthorized callback so the SPA can drop back to
// the login screen — handles the "session expired while user was on the
// dashboard" case once, instead of every page reinventing it.
//
// Bodies are parsed as JSON unless the response is 204 No Content.

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

// Module-level callback installed once at App boot. The default no-ops so
// pre-boot calls (rare) don't crash. The App registers a real handler that
// clears the meQuery cache, which causes a re-render to <LoginPage>.
let onUnauthorized: () => void = () => {};
let unauthorizedCoolingDown = false;

export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) {
    return undefined as T;
  }
  let parsed: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  if (!res.ok) {
    // Global 401 handling — fires once even if multiple in-flight calls all
    // see 401 at the same moment (cooldown ensures we don't dispatch a
    // hundred state updates in one tick).
    if (res.status === 401 && path !== "/api/v1/login" && path !== "/api/v1/me") {
      if (!unauthorizedCoolingDown) {
        unauthorizedCoolingDown = true;
        try {
          onUnauthorized();
        } finally {
          setTimeout(() => {
            unauthorizedCoolingDown = false;
          }, 1000);
        }
      }
    }
    const msg =
      (parsed &&
      typeof parsed === "object" &&
      "error" in parsed &&
      typeof (parsed as { error: string }).error === "string"
        ? (parsed as { error: string }).error
        : `${method} ${path} failed`) || res.statusText;
    throw new ApiError(res.status, msg, parsed);
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
};

// --- typed shapes mirroring the Go server's response models ---

export interface SafeUser {
  id: string;
  username: string;
  role: "admin" | "operator";
  mustChangePassword: boolean;
  disabled: boolean;
  createdAt: string;
}

export interface ConnectorSummary {
  id: string;
  displayName: string;
  schema: ConfigSchema;
}

export interface ConfigSchema {
  fields: FieldDef[];
}

export interface FieldDef {
  key: string;
  label: string;
  type:
    "string" | "secret" | "file" | "int" | "bool" | "enum" | "textarea" | "radio-group" | "time";
  required?: boolean;
  default?: unknown;
  placeholder?: string;
  help?: string;
  options?: {
    value: string;
    label: string;
    subtitle?: string; // second line shown inside the tab/card
    description?: string; // paragraph shown below the group when this option is active
  }[];
  visibleWhen?: { field: string; equals: string };
  // Lowercase, dot-prefixed extensions accepted for "file" fields
  // (e.g. [".json"], [".l5x", ".l5k", ".csv"]). Empty/undefined = any.
  accept?: string[];
  // When true, render this field on the same row as the previous inline field.
  inline?: boolean;
}

// UserStructuredProps mirrors devprops.UserStructuredProps (Go) — the
// operator-supplied structured properties attached to every Dataset a
// source emits. quickAdd is keyed by the devprops.* PropertyID (see
// QUICK_ADD_SPECS in ./quickAddProps, which must stay in sync with
// internal/devprops/quickadd.go).
export interface UserStructuredProps {
  firmwareVersion?: string;
  softwareVersion?: string;
  quickAdd?: Record<string, string>;
  custom?: CustomAttribute[];
}

export interface CustomAttribute {
  name: string;
  value: string;
}

// UploadResponse matches the agent's POST /api/v1/uploads response. The id
// is what the source-add form stores against the file field; the runner
// resolves it back to a real on-disk path at crawl time.
export interface UploadResponse {
  id: string;
  filename: string;
  sizeBytes: number;
  sha256: string;
}

// UploadListItem matches GET /api/v1/uploads — admin-only. SourceID empty
// means the upload is unattached (orphan; will be janitored after 24h).
export interface UploadListItem {
  id: string;
  filename: string;
  sizeBytes: number;
  sha256: string;
  createdAt: string;
  createdBy?: string;
  sourceId?: string;
}

// StorageBreakdown matches GET /api/v1/storage. Sizes in bytes; the UI
// formats. ArtifactsByState may be missing or empty when no artifacts yet.
export interface StorageBreakdown {
  dataDir: string;
  totalBytes: number;
  dbBytes: number;
  uploadsBytes: number;
  uploadsCount: number;
  artifactsBytes: number;
  artifactsCount: number;
  artifactsByState?: Record<string, number>;
  runLogsBytes: number;
  oldestDelivered?: string;
}

export interface SetupStatus {
  setupComplete: boolean;
}

export interface AgentIdentity {
  plantId: string;
  plantName: string;
  agentLabel: string;
  agentId?: string;
  enrolled: boolean;
}

export interface AgentStats {
  agentVersion: string;
  agentId?: string;
  plantId?: string;
  startedAt: string;
  connectorsRegistered: number;
  sources: { total: number; enabled: number };
  runs: {
    last24hSucceeded: number;
    last24hFailed: number;
    last24hRunning: number;
    lastFinishedAt?: string;
  };
  artifacts: {
    total: number;
    delivered: number;
    lastBuiltAt?: string;
    lastDeliveredAt?: string;
  };
  // The on-container path the UI pre-fills when picking the "Local disk"
  // ingest endpoint kind. Computed server-side as `${dataDir}/dropbox`.
  defaultDropboxPath?: string;
}

// --- Ingestion Preview ---

export interface PreviewSchemaField {
  path: string;
  nativeType: string;
  nullable?: boolean;
  description?: string;
  tags?: string[];
  structuredProps?: Array<{ propertyId: string; values: unknown[] }>;
}

export interface PreviewDataset {
  urn: string;
  platform: string;
  name: string;
  qualified: string;
  description?: string;
  schema?: PreviewSchemaField[];
  tags?: string[];
  owners?: Array<{ urn: string; type?: string }>;
  properties?: Record<string, string>;
  structuredProps?: Array<{ propertyId: string; values: unknown[] }>;
  subTypes?: string[];
  container?: string;
  origin?: string;
  // Per-dataset tag cap metadata (added by previewDatasetEntry on the server).
  // fieldsTotal is the real count before any truncation; use it for the
  // summary stat and the "showing N of M" notice in the UI.
  fieldsTotal?: number;
  fieldsTruncated?: boolean;
}

export interface PreviewContainer {
  urn: string;
  platform: string;
  name: string;
  parent?: string;
  subType?: string;
  origin?: string;
  properties?: Record<string, string>;
}

export interface PreviewLineageEdge {
  upstreamUrn: string;
  downstreamUrn: string;
  type: string;
}

export interface PreviewDataFlow {
  urn: string;
  orchestrator: string;
  flowId: string;
  cluster: string;
  name: string;
  description?: string;
  tags?: string[];
}

export interface PreviewDataJob {
  urn: string;
  flowUrn: string;
  jobId: string;
  name: string;
  description?: string;
  tags?: string[];
}

export interface PreviewWarning {
  severity: "error" | "warn" | "info";
  entity: string;
  message: string;
}

export interface PreviewLog {
  level: string;
  message: string;
}

export interface PreviewSummary {
  datasetCount: number;
  containerCount: number;
  fieldCount: number;
  lineageCount: number;
  dataFlowCount: number;
  dataJobCount: number;
  warningCount: number;
}

export interface PreviewFieldsPage {
  fields: PreviewSchemaField[];
  total: number;
  offset: number;
  limit: number;
}

export interface PreviewResult {
  sourceId: string;
  connectorId: string;
  summary: PreviewSummary;
  datasets: PreviewDataset[];
  containers: PreviewContainer[];
  lineageEdges: PreviewLineageEdge[];
  dataFlows: PreviewDataFlow[];
  dataJobs: PreviewDataJob[];
  warnings: PreviewWarning[];
  logs: PreviewLog[];
  durationMs: number;
  truncated: boolean;
}
